//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { UserSessionService } from '../../../../services/user-session'
import { TypeHelper, LocalizationHelper, TemplateHelper, ConfigHelper } from '../../../../helpers'
import { ValidationControlsService } from '../../../../services/controls'

import { IMRSelectGridPopupOptions, IIMRSelectGridPopup, IMRSelectGridPopupInitializedArgs } from '../popup'

import { IIMRSelectGridInputField, IMRSelectGridInputFieldOptions } from './select-grid.input-field.interfaces'
import { IMRSelectGridSelectionChanged } from '../select-grid/select-grid.interfaces'
import { IMRBaseControlController } from '../../base'

export class IMRSelectGridInputFieldController<TObject>
    extends IMRBaseControlController<IIMRSelectGridInputField<TObject>, IMRSelectGridInputFieldOptions<TObject>>
    implements IIMRSelectGridInputField<TObject>
{
    // #region Members

    public id: string

    private textBox: DevExpress.ui.dxTextBox
    public textBoxOptions: DevExpress.ui.dxTextBoxOptions
    public textBoxButtonOptions: DevExpress.ui.dxButtonOptions

    public validatorOptions: DevExpress.ui.dxValidatorOptions
    private validator: DevExpress.ui.dxValidator

    private _selectGridPopover: IIMRSelectGridPopup<TObject>
    public selectGridPopoverOptions: IMRSelectGridPopupOptions<TObject>

    private customButtonTemplate: string

    // #endregion

    // #region Ctor

    public constructor(
        private userSessionService: UserSessionService,
        private validationControlsService: ValidationControlsService
    ) {
        super()
    }

    // #endregion

    // #region IIMRSelectGridInputField Implementation

    public get selectGridPopover(): IIMRSelectGridPopup<TObject> {
        return this._selectGridPopover
    }

    public isValid(): boolean {
        if (this.validator != null) {
            const validationResult: DevExpress.ui.dxValidationGroupResult = this.validator.validate()

            if (validationResult != null) {
                return validationResult.isValid
            }
        }

        return true
    }

    // #endregion


    // #region Component

    protected initialize(): void {
        this.setDefaults()
        this.setOptions()
        this.initializeGridSelectPopover()
        this.initializeTextBox()
        this.initializeTextBoxButton()
        this.initializeValidation()
    }

    // #endregion

    // #region Text Box

    private initializeTextBox(): void {
        this.textBoxOptions = {
            width: '100%',
            placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),
            readOnly: true,
            value: null,

            onInitialized: (args: any) => { this.textBox = args.component },
            onFocusIn: () => { this.selectGridPopover.getPopover().show() }
        }
    }

    private initializeTextBoxButton() {
        this.textBoxButtonOptions = {
            template: this.customButtonTemplate != null ? this.customButtonTemplate : TemplateHelper.getIcon(ConfigHelper.Icon.HEADLINE, this.userSessionService),

            onClick: () => {
                this.selectGridPopover.getPopover().toggle(!this.selectGridPopover.getPopover().isVisible())
            }
        }
    }

    private updateSelectionText(selectedItems: TObject[]) {
        const selectedText: string = _.chain(selectedItems)
            .map((object: TObject) => {
                return object.toString()
            })
            .join('; ')
            .value()

        this.textBox.option('value', selectedText)
    }

    private getTextBoxValidationRules(): any[] {
        const validationRules: any[] = []

        if (this.options.isRequired) {
            validationRules.push(this.validationControlsService.getRequiredRule())
        }

        return validationRules
    }

    // #endregion

    // #region Grid Select Popup

    private initializeGridSelectPopover() {
        this.selectGridPopoverOptions = this.overwritePopoverOptions(this.options.selectGridPopoverOptions)
    }

    private onSelectGridPopoverInitialized(component: IIMRSelectGridPopup<TObject>) {
        this._selectGridPopover = component;
    }

    private onSelectGridPopoverSelectionChanged(objects: TObject[]) {
        this.updateSelectionText(objects)
    }

    private onGridSelectPopoverCloseOnOutsideClick(e: any): boolean {
        return !this.textBox
            .element()
            .get(0)
            .contains(e.target)
    }

    private overwritePopoverOptions(options: IMRSelectGridPopupOptions<TObject>): IMRSelectGridPopupOptions<TObject> {
        if (options == null) {
            options = {} as IMRSelectGridPopupOptions<TObject>
        }

        if (options.popoverOptions == null) {
            options.popoverOptions = {
                innerOptions: {}
            }
        }

        if (options.popoverOptions.innerOptions == null) {
            options.popoverOptions.innerOptions = {}
        }

        options.popoverOptions.innerOptions.target = `#imr-select-grid-input-field-button_${this.id}`

        if (options.popoverOptions.innerOptions.closeOnOutsideClick == null || options.popoverOptions.innerOptions.closeOnOutsideClick) {
            options.popoverOptions.innerOptions.closeOnOutsideClick = e => this.onGridSelectPopoverCloseOnOutsideClick(e)
        }

        options.onInitialized = TypeHelper.wrapAndCall(options.onInitialized, (e: IMRSelectGridPopupInitializedArgs<TObject>) => {
            this.onSelectGridPopoverInitialized(e.component)
        })

        options.selectGridOptions.onSelectionChanged = TypeHelper.wrapAndCall(options.selectGridOptions.onSelectionChanged, (e: IMRSelectGridSelectionChanged<TObject>) => {
            this.onSelectGridPopoverSelectionChanged(e.selectedObjects)
        })

        return options
    }

    // #endregion

    // #region Validator

    private initializeValidation(): void {
        this.validatorOptions = {
            validationRules: this.getTextBoxValidationRules(),

            onInitialized: (args: any) => { this.validator = args.component }
        }
    }

    // #endregion

    // #region Options

    private setDefaults(): void {
        this.selectGridPopoverOptions = null
    }

    private setOptions(): void {
        if (this.options != null) {
            if (this.options.selectGridPopoverOptions != null) {
                this.selectGridPopoverOptions = TypeHelper.clone(this.options.selectGridPopoverOptions, true)
            }

            if (this.options.customButtonTemplate != null) {
                this.customButtonTemplate = this.options.customButtonTemplate
            }
        }
    }

    // #endregion
}